import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import Typography from '@/components/ui/typography';
import { Mail, Shield, UserCheck } from 'lucide-react';
import { Me } from '@/types/api';

export type UserProfile = Me;

interface ProfilePopupProps {
  user: UserProfile;
  isOpen: boolean;
  onClose: () => void;
}

const ProfilePopup: React.FC<ProfilePopupProps> = ({
  user,
  isOpen,
  onClose,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Profile Information</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center space-y-6 py-4">
          {/* Avatar Section */}
          <div className="flex flex-col items-center space-y-3">
            <Avatar className="h-20 w-20">
              <AvatarImage alt={user.firstName || user.email?.split('@')[0]} />
              <AvatarFallback className="text-lg">
                {user.firstName?.charAt(0) || user.email?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <Typography variant="h3" color="neutral900" weight="bold" size="18">
              {user.firstName || user.email?.split('@')[0] || 'User'}
            </Typography>
          </div>

          {/* Profile Details */}
          <div className="w-full space-y-4">
            {/* First Name */}
            <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <UserCheck className="h-5 w-5 text-[#5536F5]" />
              <div className="flex-1">
                <Typography
                  variant="p"
                  color="neutral500"
                  weight="normal"
                  size="12"
                >
                  First Name
                </Typography>
                <Typography
                  variant="p"
                  color="neutral900"
                  weight="medium"
                  size="14"
                >
                  {user.firstName || 'Not provided'}
                </Typography>
              </div>
            </div>

            {/* Last Name */}
            <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <UserCheck className="h-5 w-5 text-[#5536F5]" />
              <div className="flex-1">
                <Typography
                  variant="p"
                  color="neutral500"
                  weight="normal"
                  size="12"
                >
                  Last Name
                </Typography>
                <Typography
                  variant="p"
                  color="neutral900"
                  weight="medium"
                  size="14"
                >
                  {user.lastName || 'Not provided'}
                </Typography>
              </div>
            </div>

            {/* Username */}

            {/* Email */}
            <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <Mail className="h-5 w-5 text-[#5536F5]" />
              <div className="flex-1">
                <Typography
                  variant="p"
                  color="neutral900"
                  weight="medium"
                  size="14"
                >
                  {user.email || 'Not provided'}
                </Typography>
                {/* {user.email_verified && (
                  <Typography variant="p" color="success" weight="normal" size="12">
                    ✓ Verified
                  </Typography>
                )} */}
              </div>
            </div>

            {/* Role */}
            <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <Shield className="h-5 w-5 text-[#5536F5]" />
              <div className="flex-1">
                <Typography
                  variant="p"
                  color="neutral500"
                  weight="normal"
                  size="12"
                >
                  Role
                </Typography>
                <Typography
                  variant="p"
                  color="neutral900"
                  weight="medium"
                  size="14"
                >
                  {user.tenants?.[0]?.roles?.join(', ') || 'Not assigned'}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfilePopup;
